import { ee, ensureEarthEngineReady } from '../../services/earth-engine';
import {
  applyScaleFactors,
  applyUiBand,
  applyNdviBand,
  applyMndwiBand,
  filterElevation,
  maskCloudLandsat,
  maskCloudSentinel,
  trainAndClassify,
  getMapBiomasClassificationYear,
} from './index.js';
import { mapbiomas_lulc_class_color } from '../../app/components/csqueeze/LandCover.jsx';
import { get as getSatellite } from '../../common/satellites';
import { Actions as Acquisition } from '../../store/ducks/acquisition';
import { Actions as CSqueeze } from '../../store/ducks/csqueeze';
import { buildPeriodIndex } from '../utils';
import { aoiGeometry } from '../../common/aoi';

export const SESSION_EXPORT_VERSION = 1;
export const SESSION_EXPORT_MODULE = 'csqueeze';

const LAND_COVER_COLORS = ['#687537', '#129912', '#0000ff', '#af2a2a', '#000000'];

const ELEVATION_STORAGE_KEY = 'csqueezeElevation';

/**
 * Where the acquisition step backs up the full candidate list before narrowing it to the selection
 * (`PeriodImageChooser.handleFinish`). Carried in the session so a reopened image step can still
 * offer every candidate, not just the ones that were kept.
 */
const ACQUISITION_FULL_IMAGE_LIST_KEY = 'acquisitionFullImageList';

const LANDCOVER_SHAPES_STORAGE_KEY = 'csqueezeLandCoverShapes';
const PENDING_BASELINE_STORAGE_KEY = 'csqueezeSessionImportBaseline';

/**
 * @function buildSessionExport
 * @description Bundles everything needed to redraw a completed CSqueeze land-cover classification
 * without re-clicking through the wizard. Like Bathymetry (and unlike Shoreline), CSqueeze's final
 * result is a live raster (`getThumbURL`, tied to `ee.Image` computation graphs held only in
 * Redux's `studyData` while the tab is open) — not freezable — so this exports *configuration*
 * (satellite, AOI, years/dates/missions, elevation, training polygons) and `importSessionState`
 * replays the same median-composite + classification Earth Engine calls `handleAcquireImagesMedian`
 * and `LandCover.generateClassificationLayer` would have made.
 * @param {Object} params
 * @param {Object} params.images - `state.csqueeze.studyData[0]` — per-year layers, read for their
 * `.type` (`'mapbiomas'` or custom-trained) and `.mission`.
 * @param {Array} params.years - `state.csqueeze.years`.
 * @param {Array} params.dates - `state.csqueeze.dates`.
 * @param {Array} params.missions - `state.csqueeze.missions`.
 * @param {Array<Array<Number>>} params.coordinates - AOI coordinates, `state.acquisition.coordinates`.
 * @param {Array<Object>} params.availableDates - `state.acquisition.availableDates`, the exact
 * candidate image list `handleConfirmElevation` grouped by year to build the median composites.
 * @param {Object|false} [params.baseLine] - `ProcessingPage`'s own `baseLine` local state (a
 * `{overlay: google.maps.Polyline}`, or `false` if nothing's been drawn yet).
 * @param {Number} [params.transects] - How many transects the analysis generated.
 * @param {Array<Object>} [params.transectsData] - Where each transect lies: `{transect, center, sides}`.
 * @param {Object} [params.transectsResults] - The per-year, per-transect land-cover intersections
 *  the analysis measured. Everything the result panel shows is worked out from these, so carrying
 *  them is what lets an imported session come back with its panels filled instead of empty.
 * @param {Object} [params.transectsCalcs] - Per-transect land-cover lengths per year - the table the
 *  result panel shows.
 * @param {Object} [params.transectsSqueeze] - Per-transect squeeze verdict, as the map colours it.
 * @param {Number} [params.forecastYears] - The horizon the verdicts were projected over.
 * @param {Number} [params.transectsScale] - `ProcessingPage`'s own `transectsScale` local state.
 * @param {Object} [params.classLandCoversVectors] - `state.csqueeze.classLandCoversVectors`,
 * already-evaluated GeoJSON if the user already ran "Start analysis" — free to carry over as-is.
 * @param {Array} [params.waterLandCoverVectors] - `state.csqueeze.waterLandCoverVectors`, same
 * already-evaluated situation as `classLandCoversVectors`.
 * **Called by:
 * @see {ProcessingPage} - CSqueeze's result page, via the "export session for testing" button.
 * @memberof module:Csqueeze
 * @returns {Object} A plain, JSON-serializable session snapshot.
 */
export const buildSessionExport = ({
  images,
  years,
  dates,
  missions,
  coordinates,
  availableDates,
  imageList,
  baseLine,
  baseLineCoords,
  transects,
  transectsData,
  transectsResults,
  transectsCalcs,
  transectsSqueeze,
  forecastYears,
  transectsScale,
  classLandCoversVectors,
  waterLandCoverVectors,
}) => {
  const yearTypes = {};
  Object.entries(images || {}).forEach(([year, layer]) => {
    yearTypes[year] = layer.type || '';
  });
  let landCoverShapes = {};
  try {
    landCoverShapes = JSON.parse(window.sessionStorage.getItem(LANDCOVER_SHAPES_STORAGE_KEY) || '{}');
  } catch {
    landCoverShapes = {};
  }
  let fullImageList = null;
  try {
    fullImageList = JSON.parse(window.sessionStorage.getItem(ACQUISITION_FULL_IMAGE_LIST_KEY) || 'null');
  } catch {
    fullImageList = null;
  }
  return {
    version: SESSION_EXPORT_VERSION,
    module: SESSION_EXPORT_MODULE,
    exportedAt: new Date().toISOString(),
    satelliteIndex: Number.parseInt(window.sessionStorage.getItem('selectedSatelliteIndex'), 10),
    satelliteType: window.sessionStorage.getItem('typeSatellite') || 'TOA',
    coordinates,
    years,
    dates,
    missions,
    availableDates,
    imageList: imageList?.length ? imageList : null,
    fullImageList,
    elevation: Number(window.sessionStorage.getItem(ELEVATION_STORAGE_KEY)) || 10,
    yearTypes,
    landCoverShapes,
    baseLineCoords:
      baseLineCoords?.length
        ? baseLineCoords
        : baseLine?.overlay
          ? baseLine.overlay
              .getPath()
              .getArray()
              .map((coord) => [coord.lng(), coord.lat()])
          : null,
    transects,
    transectsData: transectsData?.length ? transectsData : null,
    transectsResults: transectsResults && Object.keys(transectsResults).length ? transectsResults : null,
    transectsCalcs: transectsCalcs && Object.keys(transectsCalcs).length ? transectsCalcs : null,
    transectsSqueeze: transectsSqueeze && Object.keys(transectsSqueeze).length ? transectsSqueeze : null,
    forecastYears: forecastYears ?? null,
    transectsScale,
    classLandCoversVectors: classLandCoversVectors && Object.keys(classLandCoversVectors).length ? classLandCoversVectors : null,
    waterLandCoverVectors: waterLandCoverVectors && waterLandCoverVectors.length ? waterLandCoverVectors : null,
  };
};

/**
 * @function classifyYear
 * @description Classifies a single year's median-composite image, mirroring
 * `LandCover.generateClassificationLayer` exactly: either MapBiomas (clipped/elevation-masked,
 * no training needed) or a random-forest classifier trained on that year's drawn polygons.
 * @param {ee.Image} medianImage - The year's reconstructed median composite (UI/NDVI/MNDWI bands
 * already added).
 * @param {String} type - `'mapbiomas'` or `''` (custom-trained).
 * @param {Array<Object>} shapes - This year's training shapes, `[{landCover, ring}]` — ignored
 * when `type === 'mapbiomas'`.
 * @param {Array<Array<Number>>} coordinates - AOI coordinates (MapBiomas path only).
 * @param {String|Number} year - The year being classified (MapBiomas path only).
 * @param {Number} elevation - Elevation threshold (MapBiomas path only; the custom-trained path's
 * elevation masking already happened when the median composite was built).
 * @see {@link trainAndClassify}
 * @see {@link getMapBiomasClassificationYear}
 * **Called by:
 * @see {importSessionState}
 * @memberof module:Csqueeze
 * @returns {Object} `{ classified, classifiedMap }` — the classified `ee.Image` and its thumbnail URL.
 */
const classifyYear = (medianImage, type, shapes, coordinates, year, elevation) => {
  const bands = ['UI', 'NDVI', 'MNDWI'];
  if (type === 'mapbiomas') {
    const classified = getMapBiomasClassificationYear(coordinates, year, elevation);
    const classifiedMap = classified.sldStyle(mapbiomas_lulc_class_color).getThumbURL({
      format: 'png',
      dimensions: 800,
      tileScale: 512,
    });
    return { classified, classifiedMap };
  }
  const features = (shapes || []).map((shape) => ({
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [shape.ring] },
    properties: { LandCover: shape.landCover },
  }));
  const classificationAreas = ee.FeatureCollection({ type: 'FeatureCollection', features });
  const classified = trainAndClassify(medianImage, classificationAreas, bands);
  const classifiedMap = classified
    .visualize({ palette: LAND_COVER_COLORS, min: 0, max: LAND_COVER_COLORS.length - 1 })
    .getThumbURL({ format: 'png', dimensions: 800, tileScale: 512 });
  return { classified, classifiedMap };
};

/**
 * @function buildMedianImage
 * @description Rebuilds a single year's median-composite image, mirroring
 * `handleAcquireImagesMedian` (the CSqueeze saga) exactly: mosaics that year's images, adds the
 * UI/NDVI/MNDWI bands, applies Landsat scale factors if needed, masks by elevation and clouds, and
 * clips to the AOI.
 * @param {Array<Object>} infos - That year's image entries (from `availableDates`, each `{date, name}`).
 * @param {Object} satellite - `getAcquisitionParameters`-shaped satellite object (has `.get(name)`).
 * @param {ee.Geometry} geometry - AOI geometry.
 * @param {Number} elevation - Elevation threshold.
 * @see {@link handleAcquireImagesMedian} - the saga this mirrors.
 * **Called by:
 * @see {importSessionState}
 * @memberof module:Csqueeze
 * @returns {Object} `{ medianImage, mission, url, falseColorUrl }`.
 */
const buildMedianImage = (infos, satellite, geometry, elevation) => {
  let mission, missionInfo;

  const imagesEE = ee.List(
    infos.map((info) => {
      const date = info.date;
      const end = new Date(date);
      end.setDate(end.getDate() + 1);
      mission = satellite.get(info.name);
      missionInfo = info.name.split('/');
      return ee.ImageCollection(info.name).filterBounds(geometry).filter(ee.Filter.date(date, end)).first();
    }),
  );

  let medianImage = ee.ImageCollection.fromImages(imagesEE).filterBounds(geometry);
  medianImage = medianImage.map((img) =>
    img
      .addBands(applyUiBand(img, mission.bands))
      .addBands(applyNdviBand(img, mission.bands))
      .addBands(applyMndwiBand(img, mission.bands)),
  );

  const bands = [mission.bands.red, mission.bands.green, mission.bands.blue];
  const bandsFalseColor = [mission.bands.nir, mission.bands.green, mission.bands.blue];

  if (missionInfo[0] === 'LANDSAT') medianImage = medianImage.map(applyScaleFactors);
  medianImage = medianImage
    .map((img) => filterElevation(img, elevation, null, mission.shortname))
    .map((img) => (mission.shortname === 'S2' ? maskCloudSentinel(img, mission) : maskCloudLandsat(img, mission.bands.qa, geometry)))
    .mosaic()
    .clip(geometry);

  let medianImageVisualize = medianImage.visualize({ bands, min: 0.0, max: 0.3 });
  if (missionInfo[0] === 'LANDSAT') medianImageVisualize = medianImageVisualize.rename(bands);
  const url = medianImageVisualize.getThumbURL({ format: 'png', dimensions: 800, tileScale: 512 });

  let falseColorVisualize = medianImage.visualize({ bands: bandsFalseColor, min: 0.0, max: 0.3 });
  if (missionInfo[0] === 'LANDSAT') falseColorVisualize = falseColorVisualize.rename(bands);
  const falseColorUrl = falseColorVisualize.getThumbURL({ format: 'png', dimensions: 800, tileScale: 512 });

  return { medianImage, mission, url, falseColorUrl };
};

/**
 * @function importSessionState
 * @description Replays the Earth Engine calls `handleAcquireImagesMedian` (median composite) and
 * `LandCover.generateClassificationLayer` (classification) would have made for the exported
 * configuration, for every year, then dispatches the result into `state.csqueeze.studyData` in
 * exactly the shape `LandCover.handleFinish` leaves behind. This genuinely re-runs Earth Engine —
 * what's skipped is the wizard (AOI redraw, year/image selection, manually drawing training
 * polygons or picking MapBiomas per year), not the computation.
 * @param {Function} dispatch - Redux dispatch.
 * @param {Object} exported - A session snapshot produced by `buildSessionExport`.
 * @see {@link buildSessionExport}
 * **Called by:
 * @see {CoastalSqueezePage} - via the "import session for testing" icon's onImport.
 * @memberof module:Csqueeze
 * @returns {Promise<void>}
 */
export const importSessionState = async (dispatch, exported) => {
  await ensureEarthEngineReady();
  dispatch(Acquisition.reset());
  const {
    satelliteIndex,
    satelliteType,
    coordinates,
    years,
    dates,
    missions,
    availableDates,
    imageList,
    fullImageList,
    elevation,
    yearTypes,
    landCoverShapes,
    baseLineCoords,
    transects,
    transectsData,
    transectsResults,
    forecastYears,
    transectsScale,
    classLandCoversVectors,
    waterLandCoverVectors,
  } = exported;

  dispatch(Acquisition.setSatellite(satelliteIndex, satelliteType));
  const geometry = aoiGeometry(coordinates);
  dispatch(Acquisition.setAOI(null, coordinates, geometry, null));
  dispatch(Acquisition.setAvailableDates(availableDates, missions));
  dispatch(CSqueeze.setYears(years));
  dispatch(CSqueeze.setDates(dates));
  dispatch(CSqueeze.setMissions(missions));

  window.sessionStorage.setItem(ELEVATION_STORAGE_KEY, String(elevation));
  window.sessionStorage.setItem(LANDCOVER_SHAPES_STORAGE_KEY, JSON.stringify(landCoverShapes || {}));

  const satellite = getSatellite(satelliteIndex, false);

  const bounds = new window.google.maps.LatLngBounds();
  coordinates.forEach(([lng, lat]) => bounds.extend({ lat, lng }));

  const periods = imageList?.length
    ? buildPeriodIndex(imageList)
    : {
        entries: Object.entries(
          (availableDates || []).reduce((acc, item) => {
            const year = item.date.slice(0, 4);
            acc[year] = acc[year] || [];
            acc[year].push(item);
            return acc;
          }, {}),
        ),
        labels: {},
      };

  dispatch(CSqueeze.setPeriodLabels(periods.labels));
  dispatch(CSqueeze.resetImageList());
  if (imageList?.length) {
    imageList.forEach((infos) => dispatch(CSqueeze.setImageList(infos)));
  }
  if (fullImageList) {
    window.sessionStorage.setItem(ACQUISITION_FULL_IMAGE_LIST_KEY, JSON.stringify(fullImageList));
  }

  const layers = {};
  periods.entries.forEach(([year, infos]) => {
    const { medianImage, mission, url, falseColorUrl } = buildMedianImage(infos, satellite, geometry, elevation);
    const type = yearTypes?.[year] || '';
    const { classified, classifiedMap } = classifyYear(
      medianImage,
      type,
      landCoverShapes?.[year],
      coordinates,
      year,
      elevation,
    );
    layers[year] = {
      url,
      falseColorUrl,
      year,
      date: year,
      name: year,
      missionName: mission.name,
      mission,
      imageObject: medianImage,
      overlay: new window.google.maps.GroundOverlay(url, bounds),
      overlayFalseColor: new window.google.maps.GroundOverlay(falseColorUrl, bounds),
      classification: new window.google.maps.GroundOverlay(classifiedMap, bounds),
      type,
      classified,
    };
  });

  dispatch(CSqueeze.setStudyData([layers, {}]));

  dispatch(CSqueeze.resetWaterLandCover());
  dispatch(CSqueeze.resetClassLandCovers());

  if (classLandCoversVectors) {
    Object.entries(classLandCoversVectors).forEach(([year, vectors]) => {
      dispatch(CSqueeze.setClassLandCovers(year, vectors));
    });
  }
  if (waterLandCoverVectors) {
    waterLandCoverVectors.forEach((entry) => dispatch(CSqueeze.setWaterLandCover(entry)));
  }

  if (baseLineCoords) {
    window.sessionStorage.setItem(
      PENDING_BASELINE_STORAGE_KEY,
      JSON.stringify({
        baseLineCoords,
        transects,
        transectsData,
        transectsResults,
        forecastYears,
        transectsScale,
      }),
    );
  } else {
    window.sessionStorage.removeItem(PENDING_BASELINE_STORAGE_KEY);
  }
};

/**
 * @function readPendingBaseline
 * @description Reads and clears the baseline/transect-count/transect-scale data
 * `importSessionState` left waiting (see `PENDING_BASELINE_STORAGE_KEY`'s comment), if any. A
 * no-op read (returns `null`) if nothing is pending, so it's safe to call unconditionally.
 * @see {@link importSessionState}
 * **Called by:
 * @see {ProcessingPage} - CSqueeze's result page, once its map has mounted.
 * @memberof module:Csqueeze
 * @returns {Object|null} `{ baseLineCoords, transects, transectsScale }`, or `null`.
 */
export const readPendingBaseline = () => {
  const raw = window.sessionStorage.getItem(PENDING_BASELINE_STORAGE_KEY);
  if (!raw) return null;
  window.sessionStorage.removeItem(PENDING_BASELINE_STORAGE_KEY);
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};
